import React, { useState, useCallback, useRef } from "react";
import { Modal, Button, Spin } from "antd";
import axios from "axios";
import { PATH } from "../../../redux/utils";
import { DataBadge } from "../../../components/ui";

const authHeader = () => {
  const token = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const apiService = axios.create({ baseURL: PATH });

const ACCEPTED_TYPES = [
  "image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp",
  "application/pdf",
  "application/zip", "application/x-zip-compressed", "application/x-zip",
];
const ACCEPTED_EXT = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".pdf", ".zip"];
const MAX_GB = 2;
const MAX_MB = MAX_GB * 1024;

const PRIMARY = "#0b1b2b";

const CAMPO_LABELS = {
  estado:               "Estado",
  ciudad:               "Ciudad",
  sala:                 "Sala",
  curp:                 "CURP",
  expediente:           "Expediente",
  fecha_emision:        "Fecha de emisión",
  solicitante:          "Solicitante",
  citado:               "Citado",
  objeto_solicitud:     "Objeto de solicitud",
  fecha_audiencia:      "Fecha de audiencia",
  hora_audiencia:       "Hora de audiencia",
  conciliador:          "Conciliador",
  apercibimiento_multa: "Apercibimiento / multa",
};

// Orden visual deseado: datos de identidad primero, luego audiencia, luego extras
const CAMPO_ORDER = [
  "estado", "ciudad", "sala",
  "expediente", "curp",
  "solicitante", "citado", "objeto_solicitud",
  "fecha_emision", "fecha_audiencia", "hora_audiencia",
  "conciliador", "apercibimiento_multa",
];

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

function FileIcon({ name, size = 28 }) {
  const ext = name ? name.slice(name.lastIndexOf(".")).toLowerCase() : "";
  const style = { fontSize: size, flexShrink: 0 };
  if ([".jpg", ".jpeg", ".png", ".gif", ".webp"].includes(ext))
    return <i className="bi bi-file-image" style={{ ...style, color: "#6366f1" }} />;
  if (ext === ".pdf")
    return <i className="bi bi-file-pdf" style={{ ...style, color: "#ef4444" }} />;
  if (ext === ".zip")
    return <i className="bi bi-file-zip" style={{ ...style, color: "#f59e0b" }} />;
  return <i className="bi bi-file-earmark" style={{ ...style, color: "#6b7280" }} />;
}

/* ── Detecta si el tipo_doc incluye un estado conocido ── */
function estadoBadge(tipo_doc) {
  if (!tipo_doc) return null;
  if (tipo_doc.includes("Sonora"))
    return <DataBadge color="blue" className="ms-2">Sonora</DataBadge>;
  if (tipo_doc.includes("Baja California"))
    return <DataBadge color="purple" className="ms-2">Baja California</DataBadge>;
  return <DataBadge color="default" className="ms-2">Estado no identificado</DataBadge>;
}

/* ── Tarjeta de resultado por archivo ── */
function ArchivoResultCard({ archivo, campos = {}, tipo_doc, error, labels }) {
  const allLabels = { ...CAMPO_LABELS, ...(labels || {}) };

  // Ordenar campos: primero los de CAMPO_ORDER que tengan valor, luego el resto con valor
  const ordenados = CAMPO_ORDER.filter((k) => campos[k] != null && campos[k] !== "");
  const extras    = Object.keys(campos).filter((k) => !CAMPO_ORDER.includes(k) && campos[k] != null && campos[k] !== "");
  const conValor  = [...ordenados, ...extras];
  const sinValor  = Object.keys(allLabels).filter((k) => !conValor.includes(k));

  const hasError = !!error;

  return (
    <div
      style={{
        border: `1.5px solid ${hasError ? "#fecaca" : "#e5e7eb"}`,
        overflow: "hidden",
        background: "#fff",
      }}
    >
      {/* ── cabecera ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 16px",
          background: hasError ? "#fef2f2" : "#f8fafc",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <FileIcon name={archivo} size={22} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#111827",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={archivo}
          >
            {archivo}
          </div>
          {tipo_doc && !hasError && (
            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
              {tipo_doc}
              {estadoBadge(tipo_doc)}
            </div>
          )}
        </div>
        {hasError ? (
          <i className="bi bi-exclamation-triangle-fill" style={{ color: "#ef4444", fontSize: 18 }} />
        ) : (
          <DataBadge color={conValor.length >= 5 ? "green" : conValor.length > 0 ? "orange" : "default"}>
            {conValor.length}/{Object.keys(allLabels).length} campos
          </DataBadge>
        )}
      </div>

      {/* ── error ── */}
      {hasError && (
        <div style={{ padding: "12px 16px", fontSize: 13, color: "#991b1b" }}>
          {error}
        </div>
      )}

      {/* ── campos encontrados ── */}
      {!hasError && conValor.length > 0 && (
        <div style={{ padding: "14px 16px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: "14px 20px",
            }}
          >
            {conValor.map((key) => (
              <div key={key}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    marginBottom: 3,
                  }}
                >
                  {allLabels[key] || key}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "#111827",
                    fontWeight: 500,
                    lineHeight: 1.4,
                    wordBreak: "break-word",
                  }}
                >
                  {campos[key]}
                </div>
              </div>
            ))}
          </div>

          {/* campos no encontrados, colapsados */}
          {sinValor.length > 0 && (
            <>
              <hr className="my-3" />
              <details>
                <summary style={{ fontSize: 11, color: "#9ca3af", cursor: "pointer" }}>
                  {sinValor.length} campo{sinValor.length > 1 ? "s" : ""} no encontrado{sinValor.length > 1 ? "s" : ""}
                </summary>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                  {sinValor.map((key) => (
                    <DataBadge key={key} color="default">
                      {allLabels[key] || key}
                    </DataBadge>
                  ))}
                </div>
              </details>
            </>
          )}
        </div>
      )}

      {!hasError && conValor.length === 0 && (
        <div style={{ padding: "14px 16px", fontSize: 13, color: "#9ca3af" }}>
          No se encontraron campos reconocibles en este archivo.
        </div>
      )}
    </div>
  );
}

/* ══════════════════ Modal principal ══════════════════ */
export default function ImportarAIModal({ open, onClose, onSuccess, context = {} }) {
  const [file, setFile]         = useState(null);
  const [dragging, setDragging] = useState(false);
  const [phase, setPhase]       = useState("idle"); // idle | loading | done | error
  const [errorMsg, setErrorMsg] = useState("");
  const [results, setResults]   = useState([]);
  const fileInputRef            = useRef(null);

  const reset = useCallback(() => {
    setFile(null);
    setDragging(false);
    setPhase("idle");
    setErrorMsg("");
    setResults([]);
  }, []);

  const handleClose = () => {
    if (phase === "loading") return;
    reset();
    onClose();
  };

  const validate = (f) => {
    if (!f) return "No se seleccionó ningún archivo.";
    const ext = f.name.slice(f.name.lastIndexOf(".")).toLowerCase();
    if (!ACCEPTED_TYPES.includes(f.type) && !ACCEPTED_EXT.includes(ext))
      return "Formato no válido. Se aceptan: imagen, PDF, ZIP.";
    if (f.size > MAX_MB * 1024 * 1024)
      return `El archivo supera el límite de ${MAX_GB} GB.`;
    return null;
  };

  const pickFile = (f) => {
    const err = validate(f);
    if (err) { setErrorMsg(err); setFile(null); return; }
    setFile(f);
    setPhase("idle");
    setErrorMsg("");
    setResults([]);
  };

  const onDragOver  = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) pickFile(f);
  };
  const onInputChange = (e) => {
    const f = e.target.files?.[0];
    if (f) pickFile(f);
    e.target.value = "";
  };

  const handleAccept = async () => {
    if (!file || phase === "loading") return;
    setPhase("loading");
    setErrorMsg("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      Object.entries(context).forEach(([k, v]) => {
        if (v !== undefined && v !== null) formData.append(k, v);
      });

      const { data } = await apiService.post(
        "conciliaciones/importar-ai",
        formData,
        { headers: { ...authHeader(), "Content-Type": "multipart/form-data" } }
      );

      setResults(data?.archivos || []);
      setPhase("done");
      if (typeof onSuccess === "function") onSuccess(data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Error al procesar el archivo con IA.";
      setPhase("error");
      setErrorMsg(msg);
    }
  };

  const isDone   = phase === "done";
  const totalOk  = results.filter((r) => !r.error).length;
  const totalErr = results.filter((r) =>  r.error).length;

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      maskClosable={phase !== "loading"}
      closable={phase !== "loading"}
      centered
      width={760}
      title={
        <span className="d-flex align-items-center gap-2">
          <div
            style={{
              width: 36, height: 36,
              background: PRIMARY,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}
          >
            <i className="bi bi-robot" style={{ color: "#fff", fontSize: 18 }} />
          </div>
          <div>
            <div className="fs-6">Importar con IA</div>
            <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>
              OCR automático · imagen, PDF o ZIP
            </div>
          </div>
        </span>
      }
      styles={{ body: { maxHeight: "72vh", overflowY: "auto" } }}
      footer={[
        <Button key="close" onClick={handleClose} disabled={phase === "loading"}>
          {isDone ? "Cerrar" : "Cancelar"}
        </Button>,
        !isDone && (
          <Button
            key="accept"
            type="primary"
            style={{ background: PRIMARY, borderColor: PRIMARY }}
            onClick={handleAccept}
            disabled={!file || phase === "loading"}
            loading={phase === "loading"}
          >
            {phase === "loading" ? "Procesando…" : "Aceptar"}
          </Button>
        ),
        isDone && (
          <Button key="again" onClick={reset}>
            Procesar otro
          </Button>
        ),
      ]}
    >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {/* ─── Drop zone ─── */}
          {!file && phase === "idle" && (
            <div
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${dragging ? PRIMARY : "#d1d5db"}`,
                padding: "52px 32px",
                textAlign: "center",
                cursor: "pointer",
                background: dragging ? "#eef2ff" : "#fafafa",
                transition: "all 0.2s ease",
                userSelect: "none",
              }}
            >
              <i
                className="bi bi-inbox"
                style={{
                  fontSize: 56,
                  color: dragging ? PRIMARY : "#9ca3af",
                  transition: "color 0.2s",
                  display: "block",
                  marginBottom: 14,
                }}
              />
              <div style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>
                Arrastra tu archivo aquí
              </div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>
                o haz clic para seleccionarlo
              </div>
              <div style={{ marginTop: 14, display: "flex", justifyContent: "center", gap: 8 }}>
                {["Imagen", "PDF", "ZIP"].map((t) => (
                  <DataBadge key={t} color="default" pill>
                    {t}
                  </DataBadge>
                ))}
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 10 }}>
                Máx. {MAX_GB} GB · solo 1 archivo
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_EXT.join(",")}
                onChange={onInputChange}
                style={{ display: "none" }}
              />
            </div>
          )}

          {/* ─── Error de validación pre-selección ─── */}
          {!file && errorMsg && (
            <div
              style={{
                padding: "10px 14px",
                background: "#fef2f2", border: "1px solid #fecaca",
                fontSize: 13, color: "#991b1b", display: "flex", alignItems: "center", gap: 8,
              }}
            >
              <i className="bi bi-exclamation-triangle-fill" style={{ color: "#ef4444" }} /> {errorMsg}
            </div>
          )}

          {/* ─── Tarjeta del archivo listo para enviar ─── */}
          {file && !isDone && (
            <div
              style={{
                display: "flex", alignItems: "center", gap: 14,
                padding: "14px 18px",
                border: `1.5px solid ${phase === "loading" ? PRIMARY + "44" : "#e5e7eb"}`,
                background: "#fff",
                transition: "border-color 0.2s",
              }}
            >
              <FileIcon name={file.name} size={32} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 700, fontSize: 14, color: "#111827",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}
                >
                  {file.name}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  {formatBytes(file.size)}
                </div>
              </div>
              {phase === "idle" && (
                <button
                  onClick={(e) => { e.stopPropagation(); reset(); }}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: "#9ca3af", fontSize: 18, lineHeight: 1, padding: 4,
                    transition: "color 0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#ef4444")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
                  title="Quitar archivo"
                >
                  ✕
                </button>
              )}
            </div>
          )}

          {/* ─── Procesando ─── */}
          {phase === "loading" && (
            <div
              style={{
                padding: "32px 24px",
                background: "#f8fafc", border: `1.5px solid ${PRIMARY}22`,
                display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
              }}
            >
              <Spin size="large" />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 15, color: "#374151", fontWeight: 700 }}>
                  La IA está analizando tu archivo…
                </div>
                <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
                  Extrayendo texto con OCR y detectando campos del citatorio
                </div>
              </div>
            </div>
          )}

          {/* ─── Error de envío ─── */}
          {phase === "error" && (
            <div
              style={{
                padding: "14px 16px",
                background: "#fef2f2", border: "1.5px solid #fecaca",
                display: "flex", gap: 10, alignItems: "flex-start",
              }}
            >
              <i className="bi bi-exclamation-triangle-fill" style={{ color: "#ef4444", fontSize: 18, marginTop: 1 }} />
              <span style={{ fontSize: 13, color: "#991b1b" }}>{errorMsg}</span>
            </div>
          )}

          {/* ─── Resultados ─── */}
          {isDone && results.length > 0 && (
            <>
              {/* resumen */}
              <div
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "12px 16px",
                  background: "#f0f9ff", border: "1.5px solid #bae6fd",
                }}
              >
                <i className="bi bi-check-circle-fill" style={{ color: "#0284c7", fontSize: 20 }} />
                <div>
                  <span style={{ fontSize: 14, color: "#0369a1", fontWeight: 700 }}>
                    {results.length} archivo{results.length > 1 ? "s" : ""} procesado{results.length > 1 ? "s" : ""}
                  </span>
                  <span style={{ fontSize: 13, color: "#0369a1" }}>
                    {" "}— {totalOk} correcto{totalOk > 1 ? "s" : ""}
                    {totalErr > 0 ? `, ${totalErr} con error` : ""}
                  </span>
                </div>
              </div>

              {/* cards */}
              {results.map((r, i) => (
                <ArchivoResultCard
                  key={i}
                  archivo={r.archivo}
                  campos={r.campos}
                  tipo_doc={r.tipo_doc}
                  error={r.error}
                  labels={r.labels}
                />
              ))}
            </>
          )}
        </div>
    </Modal>
  );
}
